import { APP_COUNTRY } from 'config';
import { basePamResponseSchema } from 'services/recharges/recharges.types';
import { z } from 'zod/v4';

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

export enum periodsCode {
  DAILY = 'DD',
  WEEKLY = 'DW',
  MONTHLY = 'DM',
}

const selfLimitationPendingListZodSchema = z
  .array(
    z.object({
      amount: z.number(),
      typeCode: z.string(),
      validityDate: z.number(),
      cluster: z.object({
        approvalAmount: z.number(),
        minAmount: z.number(),
        maxAmount: z.number(),
      }),
    }),
  )
  .optional();

const getLimitationPendingSuccessResponseSchema = basePamResponseSchema.extend({
  responseObject: z
    .object({
      accountCode: z.string(),
      selfLimitationPendingList: selfLimitationPendingListZodSchema,
    })
    .nullable(),
});

type AccountLimitationPendingIResponse = z.infer<
  typeof getLimitationPendingSuccessResponseSchema
>;

type selfLimitationPendingListType = z.infer<
  typeof selfLimitationPendingListZodSchema
>;

type AccountLimitationPendingItem =
  NonNullable<selfLimitationPendingListType>[number];

type NormalizedAccountLimitationPending = Partial<
  Record<periodsCode, AccountLimitationPendingItem>
>;

type TransformedAccountLimitationPendingResponse = ReturnType<
  typeof transformAccountLimitationPendingResponse
>;

/*-------------------------------------------------------------------------------------------------
 * transformer helper
 * -----------------------------------------------------------------------------------------------*/

const transformAccountLimitationPendingResponse = (
  response: AccountLimitationPendingIResponse,
) => {
  const list = response.responseObject?.selfLimitationPendingList ?? [];

  const normalized = list.reduce<NormalizedAccountLimitationPending>(
    (acc, item) => {
      acc[item.typeCode] = item;
      return acc;
    },
    {},
  );

  const pendingLimitsPresent =
    list.length && list.find((obj) => obj.amount) && APP_COUNTRY === 'es';

  return {
    accountCode: response.responseObject?.accountCode,
    normalized,
    list,
    pendingLimitsPresent,
  };
};

export {
  getLimitationPendingSuccessResponseSchema,
  transformAccountLimitationPendingResponse,
};
export type {
  AccountLimitationPendingIResponse,
  TransformedAccountLimitationPendingResponse,
};
